import { useRef, useState, useCallback, useEffect } from 'react'
import { Canvas, FabricImage, Text } from 'fabric'
import { cn } from '@/true-fsd/shared/utils/css'
import type {
    MarkingEditorCanvasProps,
    TextAlignment,
    MarkingPosition,
} from '../types'
import { SAFE_AREA, DEFAULT_FONT_SIZE, CANVAS, MIN_FONT_SIZE, MAX_FONT_SIZE } from '../constants'
import {
    useCanvasInitialization,
    useCanvasResize,
    useMediaLoading,
    useTextScaling,
    useTextPositioning,
    useTextObjectManagement,
} from './hooks'
import type { MediaDimensions, CanvasScale } from './types'
import { MediaErrorPlaceholder, type MediaErrorType } from '../components/MediaErrorPlaceholder'
import { MediaLoadError } from './utils/mediaUtils'
import classes from '../MarkingEditorCanvas.module.scss'

/**
 * Компонент Canvas для отображения медиа с учетом охранных полей
 * Использует Fabric.js для работы с canvas
 */
export function MarkingEditorCanvas({
    imageUrl,
    width,
    height,
    safeArea = SAFE_AREA,
    onError,
    onLoad,
    text,
    fontSize,
    textColor,
    textAlignment,
    fontFamily,
    onFontSizeChange,
    onTextChange,
    position,
    onPositionChange,
    hasBackground = false,
    backgroundColor = null,
    onScaleChange,
    initialScale,
}: MarkingEditorCanvasProps) {
    // Refs для canvas и объектов
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const fabricCanvasRef = useRef<Canvas | null>(null)
    const textObjectRef = useRef<Text | null>(null)
    const imageObjectRef = useRef<FabricImage | null>(null)

    // Refs для состояния
    const isScalingFromSliderRef = useRef(false)
    const positionRef = useRef<MarkingPosition | null>(position ?? null)
    const previousAlignmentRef = useRef<TextAlignment>(
        textAlignment ?? 'center'
    )
    const isTextJustCreatedRef = useRef(false)
    const isCreatingInRAFRef = useRef(false)
    const mediaDimensionsRef = useRef<MediaDimensions | null>(null)
    const previousScaleRef = useRef<CanvasScale | null>(null)

    // State для медиа
    const [mediaDimensions, setMediaDimensions] =
        useState<MediaDimensions | null>(null)

    // Триггер для перезапуска хуков при создании canvas
    const [canvasReadyTrigger, setCanvasReadyTrigger] = useState(0)

    // Мемоизируем обработчик для setMediaDimensions, чтобы избежать лишних ререндеров
    const handleMediaDimensionsChange = useCallback(
        (value: MediaDimensions | null) => {
            setMediaDimensions(value)
            // Триггерим перезапуск хуков при создании canvas
            setCanvasReadyTrigger((prev) => prev + 1)
        },
        []
    ) as React.Dispatch<React.SetStateAction<MediaDimensions | null>>

    // Инициализация canvas
    useCanvasInitialization({
        canvasRef,
        fabricCanvasRef,
        width,
        height,
        mediaDimensionsRef,
        setMediaDimensions: handleMediaDimensionsChange,
        initialScale,
    })

    // Загрузка медиа
    const { isLoading, error } = useMediaLoading({
        imageUrl: imageUrl ?? null,
        fabricCanvasRef,
        imageObjectRef,
        mediaDimensionsRef,
        setMediaDimensions,
        previousScaleRef,
        width,
        height,
        safeArea,
        onError,
        onLoad,
        onScaleChange,
        canvasReadyTrigger,
        initialScale,
    })

    // Обработка изменения размеров canvas
    useCanvasResize({
        canvasRef,
        fabricCanvasRef,
        imageObjectRef,
        textObjectRef,
        positionRef,
        mediaDimensionsRef,
        previousScaleRef,
        width,
        height,
        textAlignment: textAlignment ?? 'center',
        safeArea,
        onScaleChange,
    })

    // Обработка масштабирования текста
    const handleTextScaling = useTextScaling({
        textObjectRef,
        fabricCanvasRef,
        isScalingFromSliderRef,
        onFontSizeChange,
        safeArea,
        hasBackground,
        backgroundColor,
    })

    // Обработка позиционирования текста
    const handleTextModified = useTextPositioning({
        textObjectRef,
        fabricCanvasRef,
        positionRef,
        onPositionChange,
    })

    // Управление текстовым объектом
    useTextObjectManagement({
        text: text ?? null,
        fontSize: fontSize ?? DEFAULT_FONT_SIZE,
        textColor: textColor ?? CANVAS.DEFAULT_TEXT_COLOR,
        textAlignment: textAlignment ?? 'center',
        fontFamily: fontFamily ?? 'Onest',
        position: position ?? null,
        hasBackground,
        backgroundColor,
        fabricCanvasRef,
        textObjectRef,
        positionRef,
        previousAlignmentRef,
        isTextJustCreatedRef,
        isCreatingInRAFRef,
        isScalingFromSliderRef,
        safeArea,
        onFontSizeChange,
        onPositionChange,
        handleTextScaling,
        handleTextModified,
        canvasReadyTrigger,
        isMediaLoading: isLoading,
        imageObjectRef,
        mediaDimensions,
    })

    // Определяем тип ошибки для заглушки
    const getErrorType = (): MediaErrorType | null => {
        if (!imageUrl) {
            return 'no_media'
        }
        if (error) {
            if (error instanceof MediaLoadError) {
                return error.type === 'no_media' ? 'no_media' : error.type
            }
            // Проверяем сообщение об ошибке для определения типа
            const errorMessage = error.message.toLowerCase()
            if (
                errorMessage.includes('format') ||
                errorMessage.includes('unsupported') ||
                errorMessage.includes('invalid')
            ) {
                return 'unsupported_format'
            }
            return 'load_failed'
        }
        return null
    }

    // Ref для контейнера canvas
    const canvasContainerRef = useRef<HTMLDivElement | null>(null)

    // Обработчик прокрутки колесиком мышки для изменения размера шрифта на canvas
    useEffect(() => {
        const containerElement = canvasContainerRef.current
        const fabricCanvas = fabricCanvasRef.current
        if (!containerElement || !fabricCanvas || !onFontSizeChange) {
            return
        }

        const handleWheel = (e: WheelEvent) => {
            // Проверяем, что текст выбран (активен)
            const activeObject = fabricCanvas.getActiveObject()
            if (!activeObject || activeObject !== textObjectRef.current) {
                // Если текст не выбран, не обрабатываем событие
                return
            }

            // Проверяем, что курсор находится над canvas
            const canvasElement = canvasRef.current
            if (!canvasElement) {
                return
            }

            const rect = canvasElement.getBoundingClientRect()
            const isOverCanvas =
                e.clientX >= rect.left &&
                e.clientX <= rect.right &&
                e.clientY >= rect.top &&
                e.clientY <= rect.bottom

            if (!isOverCanvas) {
                return
            }

            // Предотвращаем стандартную прокрутку страницы и обработку Fabric.js
            e.preventDefault()
            e.stopPropagation()

            // Определяем направление прокрутки
            // deltaY > 0 - прокрутка вниз (уменьшение)
            // deltaY < 0 - прокрутка вверх (увеличение)
            const delta = e.deltaY

            // Определяем шаг изменения
            // С зажатым Ctrl/Cmd - меньший шаг (0.5px), без Ctrl/Cmd - обычный шаг (1px)
            // С зажатым Shift - больший шаг (2px)
            let step = 1
            if (e.ctrlKey || e.metaKey) {
                step = 0.5 // Более точный контроль с Ctrl/Cmd
            } else if (e.shiftKey) {
                step = 2 // Больший шаг с Shift
            }

            // Получаем текущий размер шрифта
            const currentFontSize = fontSize ?? DEFAULT_FONT_SIZE

            // Вычисляем новое значение
            let newFontSize = currentFontSize
            if (delta > 0) {
                // Прокрутка вниз - уменьшаем
                newFontSize = Math.max(MIN_FONT_SIZE, currentFontSize - step)
            } else if (delta < 0) {
                // Прокрутка вверх - увеличиваем
                newFontSize = Math.min(MAX_FONT_SIZE, currentFontSize + step)
            }

            // Округляем до целого числа, если шаг был 0.5
            if (step === 0.5) {
                newFontSize = Math.round(newFontSize * 2) / 2
            } else {
                newFontSize = Math.round(newFontSize)
            }

            // Ограничиваем значение диапазоном
            newFontSize = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, newFontSize))

            // Обновляем значение только если оно изменилось
            if (newFontSize !== currentFontSize) {
                onFontSizeChange(newFontSize)
            }
        }

        // Используем capture phase для перехвата события до Fabric.js
        containerElement.addEventListener('wheel', handleWheel, {
            passive: false,
            capture: true,
        })

        return () => {
            containerElement.removeEventListener('wheel', handleWheel, {
                capture: true,
            })
        }
    }, [
        canvasContainerRef,
        fabricCanvasRef,
        textObjectRef,
        canvasRef,
        onFontSizeChange,
        fontSize,
    ])

    const errorType = getErrorType()
    const showError = errorType !== null && !isLoading
    const showCanvas = !showError && imageUrl

    return (
        <div
            ref={canvasContainerRef}
            className={cn(classes.canvasContainer)}
        >
            {isLoading && !error && (
                <div className={classes.loading}>Загрузка медиа...</div>
            )}
            {showError && (
                <MediaErrorPlaceholder
                    errorType={errorType}
                    message={error?.message}
                />
            )}
            {showCanvas && (
                <canvas
                    ref={canvasRef}
                    className={cn(classes.canvas, {
                        [classes.canvasLoading]: isLoading,
                    })}
                />
            )}
        </div>
    )
}
